import { roundToStrike } from "@/lib/options/structure";
import type { Mode, Regime } from "@/types/options";
import { defineStrategy } from "./define";
import { leg } from "./leg";
import { dteParam, offsetParam, toggleParam } from "./params";
import { scale } from "./scale";

// ── Straddle ────────────────────────────────────────────────────────────────

export const straddle = defineStrategy("straddle", "Straddle", "volatility", {
  defaultParams: () => ({ side: -1 }),
  paramSchema: [
    toggleParam("side", "Side", [
      [-1, "Short"],
      [1, "Long"],
    ]),
  ],
  scaleParams: (_r: Regime, _m: Mode, cur: Record<string, number>) => ({
    side: cur.side ?? -1,
  }),
  toLegs: (spot, params, strikeStep) => {
    const strike = roundToStrike(spot, strikeStep);
    const s: "long" | "short" = params.side >= 0 ? "long" : "short";
    return [leg(strike, "call", s), leg(strike, "put", s)];
  },
});

// ── Strangle ────────────────────────────────────────────────────────────────

export const strangle = defineStrategy("strangle", "Strangle", "volatility", {
  defaultParams: () => ({ side: -1, callOffset: 30, putOffset: 30 }),
  paramSchema: [
    toggleParam("side", "Side", [
      [-1, "Short"],
      [1, "Long"],
    ]),
    offsetParam("callOffset", "Call Offset", 5, 100, 0),
    offsetParam("putOffset", "Put Offset", 5, 100, 1),
  ],
  scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
    side: cur.side ?? -1,
    callOffset: scale(30, r, m, true),
    putOffset: scale(30, r, m, true),
  }),
  toLegs: (spot, params, strikeStep) => {
    const center = roundToStrike(spot, strikeStep);
    const s: "long" | "short" = params.side >= 0 ? "long" : "short";
    return [
      leg(center + params.callOffset, "call", s),
      leg(center - params.putOffset, "put", s),
    ];
  },
});

// ── Time Spread (Calendar + Diagonal) ─────────────────────────────────────
// skew=0 → calendar (same strike). skew≠0 → diagonal (back leg shifted).
// Positive skew always moves the back leg toward ATM regardless of call/put.

export const timeSpread = defineStrategy("time_spread", "Time Spread", "time", {
  defaultParams: () => ({
    strikeOffset: 0,
    optionType: 0,
    skew: 0,
    frontDTE: 0,
    backDTE: 7,
  }),
  paramSchema: [
    toggleParam("optionType", "Type", [
      [0, "Call"],
      [1, "Put"],
    ]),
    offsetParam("strikeOffset", "Strike Offset", -50, 50),
    offsetParam("skew", "Back Skew", -30, 30, 1),
    dteParam("frontDTE", "Front DTE", 0, 30),
    dteParam("backDTE", "Back DTE", 1, 60),
  ],
  scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
    strikeOffset: scale(0, r, m, true, 50),
    optionType: cur.optionType ?? 0,
    skew: cur.skew ?? 0,
    frontDTE: cur.frontDTE ?? 0,
    backDTE: cur.backDTE ?? 7,
  }),
  toLegs: (spot, params, strikeStep) => {
    const frontStrike = roundToStrike(spot, strikeStep) + params.strikeOffset;
    // Positive skew → toward ATM: for calls that means lower strike, for puts higher strike
    const skewDir = params.optionType >= 1 ? 1 : -1;
    const backStrike = roundToStrike(
      frontStrike + skewDir * params.skew,
      strikeStep,
    );
    const t: "call" | "put" = params.optionType >= 1 ? "put" : "call";
    return [leg(frontStrike, t, "short"), leg(backStrike, t, "long")];
  },
});

// ── Double Time Spread (Double Calendar + Double Diagonal) ────────────────
// putSkew/callSkew=0 → double calendar. ≠0 → double diagonal.
// Positive skew moves back legs toward ATM (inward).

export const doubleTimeSpread = defineStrategy(
  "double_time_spread",
  "Double Time Spread",
  "time",
  {
    defaultParams: () => ({
      putOffset: 20,
      callOffset: 20,
      putSkew: 0,
      callSkew: 0,
      frontDTE: 0,
      backDTE: 7,
    }),
    paramSchema: [
      offsetParam("putOffset", "Put Offset", 5, 80),
      offsetParam("callOffset", "Call Offset", 5, 80),
      offsetParam("putSkew", "Put Skew", -30, 30, 1),
      offsetParam("callSkew", "Call Skew", -30, 30, 3),
      dteParam("frontDTE", "Front DTE", 0, 30),
      dteParam("backDTE", "Back DTE", 1, 60),
    ],
    scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
      putOffset: scale(20, r, m, true),
      callOffset: scale(20, r, m, true),
      putSkew: cur.putSkew ?? 0,
      callSkew: cur.callSkew ?? 0,
      frontDTE: cur.frontDTE ?? 0,
      backDTE: cur.backDTE ?? 7,
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      const putFront = center - params.putOffset;
      // Positive putSkew → toward ATM → higher strike for puts
      const putBack = roundToStrike(putFront + params.putSkew, strikeStep);
      const callFront = center + params.callOffset;
      // Positive callSkew → toward ATM → lower strike for calls
      const callBack = roundToStrike(callFront - params.callSkew, strikeStep);
      return [
        leg(putFront, "put", "short"),
        leg(putBack, "put", "long"),
        leg(callFront, "call", "short"),
        leg(callBack, "call", "long"),
      ];
    },
  },
);
